import { Stack } from "@mui/material"
import { ReactElement } from "react"
import { Control, UseFormSetError } from "react-hook-form"

import { INestedDiscimUnion01InputSpec } from "../../../../services/middleware/types/singleApplication/getSchemaTypes"
import { prepareFormProperties } from "../../../../utils/submitApplication/submitApplicationUtils"
import ValidatedAutocomplete from "../../../inputs/validated/validatedSelect"
import DisplayIf from "../../../wrappers/displayIf"
import { APPLICATION_SPACING } from "../formGenerator/formDispatcher"
import InputGenerator from "../inputGenerator/inputGenerator"

interface INestedDiscriminatedUnionInputProps<T> {
    registerNameBase: string
    discriminatorKey: string
    control: Control
    nestedFormSpec: INestedDiscimUnion01InputSpec
    watch: (key: string) => T
    setError: UseFormSetError<{
        [x: string]: object | undefined
    }>
    programmaticallySetForm: (key: string, value: T) => void
}

/**
 * This component is a recognised input. It renders a sub-form within the
 * main form. The output of this form is appended into the main form as a
 * nested JSON. This particular input is a discriminated union, meaning
 * in the sub form, the user must select which form to render based on
 * their choice of discriminator.
 * @returns
 */
const NestedDiscriminatedUnionInput = <T,>({
    registerNameBase,
    discriminatorKey,
    control,
    nestedFormSpec,
    watch,
    setError,
    programmaticallySetForm,
}: INestedDiscriminatedUnionInputProps<T>): ReactElement => {
    const generateKey = (key: string): string => `${registerNameBase}.${key}`
    const discriminators = Object.keys(nestedFormSpec.discriminator.mapping)
    const selectedDiscriminator = watch(generateKey(discriminatorKey))

    return (
        <>
            <ValidatedAutocomplete
                control={control}
                registerName={generateKey(discriminatorKey)}
                options={discriminators}
                label={
                    nestedFormSpec.oneOf[0].properties[discriminatorKey].title
                }
                required
            />

            {!!selectedDiscriminator &&
                nestedFormSpec.oneOf.map((formOptionSpec) => {
                    const { fixedValue } =
                        formOptionSpec.properties[discriminatorKey]
                            .json_schema_extra

                    return (
                        <DisplayIf
                            key={fixedValue}
                            condition={selectedDiscriminator === fixedValue}
                        >
                            <Stack spacing={APPLICATION_SPACING}>
                                {prepareFormProperties(
                                    formOptionSpec.properties
                                ).map(([key, value]) => (
                                    <InputGenerator<T>
                                        key={key}
                                        registerName={generateKey(key)}
                                        inputType={value.json_schema_extra.type}
                                        inputSpec={value}
                                        watch={watch}
                                        programmaticallySetForm={
                                            programmaticallySetForm
                                        }
                                        isAmendment={false}
                                    />
                                ))}
                            </Stack>
                        </DisplayIf>
                    )
                })}
        </>
    )
}

export default NestedDiscriminatedUnionInput
